import {
  CategoryPricing,
  CreateCategoryPricingRequest,
  CreateProductPricingRequest,
  PricingStatistics,
  ProductTypeKeywords,
  SpecificProductPricing,
  UpdateCategoryPricingRequest,
  UpdateProductPricingRequest,
  UpdateProductTypeKeywordsRequest,
} from "../types/admin";
import { ApiResponse } from "../types/common";

import apiClient from "./apiClient";

// Category Pricing
export const getCategoryPricing = async (
  pageNumber?: number,
  pageSize?: number,
  search?: string
): Promise<ApiResponse<CategoryPricing[]> | ApiResponse<any>> => {
  // If pagination params provided, use paginated endpoint
  if (pageNumber && pageSize) {
    const params = new URLSearchParams();
    params.append("pageNumber", pageNumber.toString());
    params.append("pageSize", pageSize.toString());
    if (search) params.append("search", search);
    
    const response = await apiClient.get(`/admin/pricing/categories?${params.toString()}`);
    return response.data;
  }
  
  // Otherwise use non-paginated (backward compatibility)
  const response = await apiClient.get("/admin/pricing/categories");
  return response.data;
};

export const createCategoryPricing = async (
  data: CreateCategoryPricingRequest,
): Promise<ApiResponse<CategoryPricing>> => {
  const response = await apiClient.post("/admin/pricing/categories", data);
  return response.data;
};

export const updateCategoryPricing = async (
  id: string,
  data: UpdateCategoryPricingRequest,
): Promise<ApiResponse<CategoryPricing>> => {
  const response = await apiClient.put(`/admin/pricing/categories/${id}`, data);
  return response.data;
};

export const deleteCategoryPricing = async (
  id: string,
): Promise<ApiResponse<object>> => {
  const response = await apiClient.delete(`/admin/pricing/categories/${id}`);
  return response.data;
};

// Specific Product Pricing
export const getSpecificProductPricing = async (
  pageNumber?: number,
  pageSize?: number,
  search?: string
): Promise<ApiResponse<SpecificProductPricing[]> | ApiResponse<any>> => {
  // If pagination params provided, use paginated endpoint
  if (pageNumber && pageSize) {
    const params = new URLSearchParams();
    params.append("pageNumber", pageNumber.toString());
    params.append("pageSize", pageSize.toString());
    if (search) params.append("search", search);
    
    const response = await apiClient.get(`/admin/pricing/specific-products?${params.toString()}`);
    return response.data;
  }
  
  // Otherwise use non-paginated (backward compatibility)
  const response = await apiClient.get("/admin/pricing/specific-products");
  return response.data;
};

export const createSpecificProductPricing = async (
  data: CreateProductPricingRequest,
): Promise<ApiResponse<SpecificProductPricing>> => {
  const response = await apiClient.post(
    "/admin/pricing/specific-products",
    data,
  );
  return response.data;
};

export const updateSpecificProductPricing = async (
  id: string,
  data: UpdateProductPricingRequest,
): Promise<ApiResponse<SpecificProductPricing>> => {
  const response = await apiClient.put(
    `/admin/pricing/specific-products/${id}`,
    data,
  );
  return response.data;
};

export const deleteSpecificProductPricing = async (
  id: string,
): Promise<ApiResponse<object>> => {
  const response = await apiClient.delete(
    `/admin/pricing/specific-products/${id}`,
  );
  return response.data;
};

// Statistics
export const getPricingStatistics = async (): Promise<
  ApiResponse<PricingStatistics>
> => {
  const response = await apiClient.get("/admin/pricing/statistics");
  return response.data;
};

// ProductTypeKeywords
export const getProductTypeKeywords = async (): Promise<
  ApiResponse<ProductTypeKeywords[]>
> => {
  const response = await apiClient.get("/admin/pricing/product-type-keywords");
  return response.data;
};

export const updateProductTypeKeywords = async (
  id: number,
  data: UpdateProductTypeKeywordsRequest,
): Promise<ApiResponse<ProductTypeKeywords>> => {
  const response = await apiClient.put(
    `/admin/pricing/product-type-keywords/${id}`,
    data,
  );
  return response.data;
};

export const uploadCategoryKeywords = async (
  id: string,
  file: File,
): Promise<ApiResponse<CategoryPricing>> => {
  const formData = new FormData();
  formData.append("file", file);
  const response = await apiClient.post(
    `/admin/pricing/categories/${id}/upload-keywords`,
    formData,
    {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    },
  );
  return response.data;
};

export const deleteProductTypeKeywords = async (
  id: number,
): Promise<ApiResponse<void>> => {
  const response = await apiClient.delete(
    `/admin/pricing/product-type-keywords/${id}`,
  );
  return response.data;
};

export const uploadProductTypeKeywords = async (
  id: number,
  file: File,
): Promise<ApiResponse<ProductTypeKeywords>> => {
  const formData = new FormData();
  formData.append("file", file);
  const response = await apiClient.post(
    `/admin/pricing/product-type-keywords/${id}/upload-keywords`,
    formData,
    {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    },
  );
  return response.data;
};
